import { Form, Input, Modal, Select, Switch, DatePicker } from "antd";
import { AppButton } from "components/button/AppButton";
import { useGetUserInfo } from "hooks/useGetUserInfo";
import { useQueryClient } from "react-query";
import { IdentifierProps } from "types";
import { generalValidationRules } from "utils/formHelpers/validations";
import { openNotification } from "utils/notification";
import { useUpdateCampaign } from "../hooks/useUpdateCampaign";
import { QUERY_KEY_CAMPAIGNS } from "../hooks/useGetCampaigns";
import { TMarketingCampaign } from "../types";
import dayjs from "dayjs";
import { useEffect } from "react";

type Props = IdentifierProps & { campaign: TMarketingCampaign };

export const EditCampaign = ({ open, handleClose, campaign }: Props) => {
  const [form] = Form.useForm();
  const { token } = useGetUserInfo();
  const queryClient = useQueryClient();
  const { mutate, isLoading } = useUpdateCampaign();

  useEffect(() => {
    if (campaign) {
      form.setFieldsValue({
        name: campaign.name,
        code: campaign.code,
        description: campaign.description,
        target_type: campaign.target_type,
        utm_source: campaign.utm_source,
        utm_medium: campaign.utm_medium,
        utm_campaign: campaign.utm_campaign,
        utm_term: campaign.utm_term,
        utm_content: campaign.utm_content,
        is_active: campaign.is_active,
        starts_at: campaign.starts_at ? dayjs(campaign.starts_at) : null,
        expires_at: campaign.expires_at ? dayjs(campaign.expires_at) : null,
      });
    }
  }, [campaign, form]);

  const handleSubmit = (values: any) => {
    mutate(
      {
        token,
        id: campaign.id,
        name: values.name,
        code: values.code,
        description: values.description,
        target_type: values.target_type,
        utm_source: values.utm_source,
        utm_medium: values.utm_medium,
        utm_campaign: values.utm_campaign,
        utm_term: values.utm_term,
        utm_content: values.utm_content,
        is_active: values.is_active ?? true,
        starts_at: values.starts_at
          ? dayjs(values.starts_at).toISOString()
          : null,
        expires_at: values.expires_at
          ? dayjs(values.expires_at).toISOString()
          : null,
      },
      {
        onError: (err: any) => {
          const res = err.response?.data;
          let description = "An error occurred, please try again.";
          if (res?.message && typeof res.message === "string") {
            description = res.message;
          }
          if (res?.errors && typeof res.errors === "object") {
            const first = Object.values(res.errors)[0];
            if (Array.isArray(first) && first[0]) {
              description = String(first[0]);
            }
          }
          openNotification({
            title: "Error",
            state: "error",
            description,
            duration: 6,
          });
        },
        onSuccess: () => {
          openNotification({
            title: "Success",
            state: "success",
            description: "Campaign updated successfully",
            duration: 4.5,
          });
          queryClient.invalidateQueries({ queryKey: [QUERY_KEY_CAMPAIGNS] });
          handleClose();
        },
      }
    );
  };

  return (
    <Modal
      title="Edit campaign"
      open={open}
      onCancel={() => handleClose()}
      footer={null}
      width="42rem"
      style={{ top: 10 }}
      destroyOnClose
    >
      <Form layout="vertical" requiredMark={false} form={form} onFinish={handleSubmit}>
        <Form.Item name="name" label="Campaign name" rules={generalValidationRules}>
          <Input />
        </Form.Item>
        <Form.Item
          name="code"
          label="Tracking code"
          rules={[
            { required: true, message: "Code is required" },
            {
              pattern: /^[a-zA-Z0-9\-_|]+$/,
              message: "Only letters, numbers, hyphens, underscores, pipes",
            },
          ]}
        >
          <Input />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} />
        </Form.Item>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4">
          <Form.Item name="target_type" label="Target audience" rules={generalValidationRules}>
            <Select
              options={[
                { value: "any", label: "Any" },
                { value: "talent", label: "Talent" },
                { value: "recruiter", label: "Recruiter" },
              ]}
            />
          </Form.Item>
          <Form.Item name="is_active" label="Active" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="starts_at" label="Starts at">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="expires_at" label="Expires at">
            <DatePicker showTime className="w-full" />
          </Form.Item>
          <Form.Item name="utm_source" label="UTM source">
            <Input />
          </Form.Item>
          <Form.Item name="utm_medium" label="UTM medium">
            <Input />
          </Form.Item>
          <Form.Item name="utm_campaign" label="UTM campaign">
            <Input />
          </Form.Item>
          <Form.Item name="utm_content" label="UTM content">
            <Input />
          </Form.Item>
        </div>
        <div className="flex justify-end mt-2">
          <AppButton label="Save changes" type="submit" isLoading={isLoading} />
        </div>
      </Form>
    </Modal>
  );
};
